import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { applyDefaultValues } from '../../../utils/metadata/fields';
import { reconcileRecordValues } from '../../../utils/recordFormReconciliation';

import type { EnhancedFormFieldValues } from '../../../types/record-form';
import type { DynamicRecord } from '../../../types/api';
import type { FormFieldValue } from '../../../types/forms';

interface UseRecordFormStateParams {
  record: DynamicRecord | DynamicRecord[] | null | undefined;
  picklists: Record<string, any> | undefined;
  debouncedFieldValues: Record<string, FormFieldValue>;
  fieldsBeingSaved: Set<string>;
  tableName: string;
  fieldData: any;
  preserveRecordValues?: boolean;
}

interface UseRecordFormStateResult {
  fieldValues: EnhancedFormFieldValues;
  setFieldValues: Dispatch<SetStateAction<EnhancedFormFieldValues>>;
  fieldErrors: Record<string, string | null>;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string | null>>>;
  flashingFields: Set<string>;
  setFlashingFields: Dispatch<SetStateAction<Set<string>>>;
}

export const useRecordFormState = ({
  record,
  picklists,
  debouncedFieldValues,
  fieldsBeingSaved,
  tableName,
  fieldData,
  preserveRecordValues = false,
}: UseRecordFormStateParams): UseRecordFormStateResult => {
  const [fieldValues, setFieldValues] = useState<EnhancedFormFieldValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [flashingFields, setFlashingFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Handle new record case (no record data)
    if (!record) {
      // Only set defaults on true initial load (empty state).
      // If we already have field values, the record is temporarily null due to
      // a cache invalidation/refetch cycle — keep the existing values to avoid flicker.
      setFieldValues((prev) => {
        if (Object.keys(prev).length > 0) {
          return prev; // Keep existing values during transient null
        }
        if (fieldData) {
          return applyDefaultValues(tableName, fieldData) as EnhancedFormFieldValues;
        }
        return prev;
      });
      return;
    }

    // Handle existing record data
    // Handle both single record and array cases
      const recordData = Array.isArray(record) ? record[0] : record;
      if (!recordData) return;

      const deserializedRecord: EnhancedFormFieldValues = { ...recordData };

      // Deserialize multi-select JSON fields
      Object.keys(deserializedRecord).forEach((fieldName) => {
        const picklistConfig = picklists?.[fieldName];
        if (picklistConfig && picklistConfig.type === "Multi Select") {
          try {
            deserializedRecord[fieldName] = JSON.parse(deserializedRecord[fieldName] as string || '[]');
          } catch (error) {
            console.error(`Error parsing JSON for field ${fieldName}:`, error);
            deserializedRecord[fieldName] = [];
          }
        }
      });

      // SMART UPDATE: Only update fields that aren't currently being edited
      setFieldValues((prev) => {
        const { values, remoteChangedFields } = reconcileRecordValues({
          previousValues: prev,
          serverValues: deserializedRecord,
          debouncedFieldValues,
          fieldsBeingSaved,
          preserveRecordValues,
        });
        if (remoteChangedFields.length > 0) {
          setFlashingFields(new Set(remoteChangedFields));

          // Auto-clear flash after animation completes (4 seconds)
          setTimeout(() => {
            setFlashingFields(new Set());
          }, 4000);
        }

        return values;
      });
  }, [record, picklists, debouncedFieldValues, fieldsBeingSaved, tableName, fieldData, preserveRecordValues]);

  return {
    fieldValues,
    setFieldValues,
    fieldErrors,
    setFieldErrors,
    flashingFields,
    setFlashingFields
  };
};
